import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import SellerLayout from "@/components/layouts/SellerLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import CloudinaryUpload from "@/components/CloudinaryUpload";
import { ThemeSection } from "@/components/store-builder/ThemeSection";
import { HomepageSection } from "@/components/store-builder/HomepageSection";
import { PagesSection } from "@/components/store-builder/PagesSection";
import { SocialSection } from "@/components/store-builder/SocialSection";
import { toast } from "sonner";
import { Store, ExternalLink, Crown } from "lucide-react";
import { apiError } from "@/lib/apiError";

const FIELDS = ["logo_url", "banner_url", "description", "theme_color", "layout", "announcement", "about_text", "featured_product_ids", "pages", "social", "whatsapp"];

const BrandingSection = ({ form, setField }) => (
  <div className="space-y-6">
    <div>
      <Label>Store Logo</Label>
      <p className="text-xs text-gray-500 mb-2">Square image, PNG or JPG (recommended 512×512).</p>
      <CloudinaryUpload value={form.logo_url} onChange={(url) => setField("logo_url", url)} folder="sellers/logos/" resourceType="image" testId="store-logo-upload" />
    </div>
    <div>
      <Label>Store Banner</Label>
      <p className="text-xs text-gray-500 mb-2">Wide image, PNG or JPG (recommended 1600×400).</p>
      <CloudinaryUpload value={form.banner_url} onChange={(url) => setField("banner_url", url)} folder="sellers/banners/" resourceType="image" testId="store-banner-upload" />
    </div>
    <div>
      <Label htmlFor="description">Store Tagline / Description</Label>
      <Textarea id="description" value={form.description || ""} onChange={(e) => setField("description", e.target.value)} placeholder="Tell shoppers about your brand and what you sell..." rows={3} className="mt-2" data-testid="store-description-input" />
    </div>
  </div>
);

const PreviewHeader = ({ form, info }) => {
  const accent = (info.plan?.features?.theme && form.theme_color) || "#10b981";
  return (
  <Card className="border-0 shadow-sm overflow-hidden">
    <div className="h-32 md:h-44 bg-cover bg-center relative" style={form.banner_url ? { backgroundImage: `url(${form.banner_url})` } : { backgroundColor: accent }}>
      <div className="absolute inset-0 bg-black/25" />
    </div>
    <CardContent className="relative pb-5">
      <div className="flex flex-col md:flex-row gap-4 md:items-end">
        <div className="-mt-10 w-20 h-20 rounded-2xl border-4 border-white shadow-md overflow-hidden bg-white flex items-center justify-center flex-shrink-0">
          {form.logo_url ? <img src={form.logo_url} alt="Store" className="w-full h-full object-cover" /> : <Store className="w-8 h-8" style={{ color: accent }} />}
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-heading text-xl font-bold text-gray-900">{info.business_name || "Your Store"}</h2>
          <p className="text-sm text-gray-500 truncate">{form.description || "Add a description to attract customers"}</p>
        </div>
        <Badge className="bg-secondary-100 text-secondary-700 self-start md:self-end" data-testid="store-builder-plan-badge">
          <Crown className="w-3 h-3 mr-1" />{info.plan?.name} plan
        </Badge>
        {info.store_slug && info.is_approved && (
          <Link to={`/store/${info.store_slug}`} target="_blank" className="text-sm font-medium text-primary-600 hover:text-primary-700 inline-flex items-center gap-1" data-testid="store-builder-view-store-link">
            <ExternalLink className="w-4 h-4" />View Live Store
          </Link>
        )}
      </div>
    </CardContent>
  </Card>
  );
};

const SellerStoreSettings = () => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [info, setInfo] = useState({});
  const [products, setProducts] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [settings, prods] = await Promise.all([
          axios.get(`${API}/seller/store-settings`, { headers: getAuthHeaders() }),
          axios.get(`${API}/seller/products`, { headers: getAuthHeaders() }),
        ]);
        const d = settings.data;
        setForm(Object.fromEntries(FIELDS.map((k) => [k, d[k] ?? null])));
        setInfo({ business_name: d.business_name, store_slug: d.store_slug, is_approved: d.is_approved, plan: d.plan });
        setProducts(prods.data.products || []);
      } catch (err) {
        toast.error(apiError(err, "Failed to load store settings"));
      }
      setLoading(false);
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).filter(([k, v]) => v !== null && !(k === "theme_color" && !v)));
      await axios.put(`${API}/seller/store-settings`, payload, { headers: getAuthHeaders() });
      toast.success("Store saved! Your live store is updated.");
    } catch (err) {
      toast.error(apiError(err, "Failed to save"));
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <SellerLayout title="Store Builder">
        <div className="p-12 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" /></div>
      </SellerLayout>
    );
  }

  const features = info.plan?.features || {};
  return (
    <SellerLayout title="Store Builder">
      <div className="max-w-5xl space-y-6" data-testid="store-builder-page">
        {!info.is_approved && (
          <div className="p-4 rounded-xl bg-yellow-50 border border-yellow-200 text-sm text-yellow-800" data-testid="store-builder-pending-note">
            You can design your store now. It goes live at /store/{info.store_slug} once the admin approves your seller account.
          </div>
        )}
        <PreviewHeader form={form} info={info} />
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 md:p-6">
            <Tabs defaultValue="branding">
              <TabsList className="flex flex-wrap h-auto mb-6">
                <TabsTrigger value="branding" data-testid="store-tab-branding">Branding</TabsTrigger>
                <TabsTrigger value="theme" data-testid="store-tab-theme">Theme & Layout</TabsTrigger>
                <TabsTrigger value="homepage" data-testid="store-tab-homepage">Homepage</TabsTrigger>
                <TabsTrigger value="pages" data-testid="store-tab-pages">Pages</TabsTrigger>
                <TabsTrigger value="social" data-testid="store-tab-social">Social & WhatsApp</TabsTrigger>
              </TabsList>
              <TabsContent value="branding"><BrandingSection form={form} setField={setField} /></TabsContent>
              <TabsContent value="theme"><ThemeSection form={form} setField={setField} allowed={features.theme} /></TabsContent>
              <TabsContent value="homepage"><HomepageSection form={form} setField={setField} products={products} /></TabsContent>
              <TabsContent value="pages"><PagesSection form={form} setField={setField} allowed={features.pages} maxPages={info.plan?.max_pages || 0} /></TabsContent>
              <TabsContent value="social"><SocialSection form={form} setField={setField} /></TabsContent>
            </Tabs>
            <div className="flex justify-end pt-6 mt-6 border-t">
              <Button onClick={handleSave} disabled={saving} className="bg-primary-500 hover:bg-primary-600 rounded-full px-8" data-testid="store-settings-save-btn">
                {saving ? "Saving..." : "Save & Publish"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </SellerLayout>
  );
};

export default SellerStoreSettings;
